import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const FAQSection = () => {
  const [openItems, setOpenItems] = useState(new Set());
  const { isDark } = useTheme();

  const faqItems = [
    {
      id: 1,
      question: "What is Vuzo?",
      answer: (
        <>
          Vuzo is an AI-powered web platform at <span className="font-semibold">chat.vuzo.com</span>, powered by advanced AI models including <span className="font-semibold">GPT-5.4 Pro, GPT-4o, Gemini 2.5 Flash, Claude Sonnet, and more</span>. It provides various AI tools, chat features, and task-specific AI assistants designed for productivity, creativity, and entertainment.
        </>
      ),
    },
    {
      id: 2,
      question: "On which platforms is Vuzo available?",
      answer: (
        <>
          Vuzo is available directly in your browser at <span className="font-semibold">chat.vuzo.com</span> — no download or installation required. It works on any desktop, laptop, or mobile browser.
        </>
      ),
    },
    {
      id: 3,
      question: "Can I use Vuzo on a desktop or PC?",
      answer: "Yes. Vuzo runs entirely in the browser at chat.vuzo.com — just open it on any desktop or PC and you're ready to go. No installation needed.",
    },
    {
      id: 4,
      question: "What are the main features of Vuzo?",
      answer: {
        list: [
          "AI Chat with 16+ models (GPT-5.4 Pro, Gemini 2.5 Flash, Claude Sonnet, and more)",
          "AI Image Generator for creating custom visuals",
          "AI PDF Analyzer for document processing",
          "AI-powered Assistants (Translator, AI Lawyer, AI Therapist, etc.)",
          "Starter, Pro, and Premium plans with monthly or yearly billing",
        ],
      },
    },
    {
      id: 5,
      question: "What plans does Vuzo offer?",
      answer: {
        intro: "Visit chat.vuzo.com and click Upgrade to choose from three plans:",
        list: [
          <><span className="font-semibold">Starter — $10/mo</span> — 1M tokens, 4 AI models, PDF Analyzer &amp; Vision tools</>,
          <><span className="font-semibold">Pro — $23.85/mo</span> — 2.2M tokens, 16 models (GPT-4o, Claude Sonnet + more). Start with a 14-day trial for just $2.85.</>,
          <><span className="font-semibold">Premium — $97/mo</span> — Unlimited tokens, all 18 models including Claude Opus 4.6, priority support &amp; early access.</>,
        ],
        outro: "All plans available monthly or yearly — save 20% annually. Cancel anytime.",
      },
    },
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const accentColor = isDark ? '#28F5AB' : '#177755';

  return (
    <section className="pt-[40px] lg:pt-[70px] pb-[50px] w-full">
      <div className="w-full px-4">
        <div className="text-center lg:text-left grid gap-10 lg:gap-16 lg:grid-cols-[32%_68%]">

          {/* Left */}
          <div className="space-y-4">
            <h2 className="text-[30px] md:text-[39px] font-bold leading-tight" style={{ color: accentColor }}>
              Frequently Asked Questions
            </h2>
            <p className={`text-[16px] md:text-lg ${isDark ? 'text-gray-300' : 'text-[#64748B]'}`}>
              Have questions? We've got answers.
            </p>
            <a
              href="/frequently-asked-questions-and-feedback"
              className={`inline-flex items-center px-4 py-3 text-[14px] font-medium rounded-xl transition-all duration-200 ${
                isDark
                  ? 'border border-[#28F5AB] text-[#28F5AB] hover:bg-[#28F5AB10]'
                  : 'bg-[#177755] text-white hover:bg-[#0f5b3e] shadow-sm'
              }`}
            >
              Read More
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
          </div>

          {/* Right - accordion */}
          <div className="text-left space-y-3 lg:pr-4">
            {faqItems.map((item) => {
              const isOpen = openItems.has(item.id);
              return (
                <div
                  key={item.id}
                  className={`rounded-2xl overflow-hidden transition-all duration-200 w-full lg:w-[95%] ${
                    isDark
                      ? `border ${isOpen ? 'border-[#28F5AB30] bg-[#0d1a14]' : 'border-[#1f3028] bg-[#15191A]'}`
                      : `border shadow-sm ${isOpen ? 'border-[#177755]/40 bg-white' : 'border-[#d8ede1] bg-white'}`
                  }`}
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between"
                  >
                    <span className={`font-semibold text-[15px] pr-4 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                      {item.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                      style={{ color: accentColor }}
                    />
                  </button>

                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-5">
                      {/* Left accent bar */}
                      <div className="flex gap-3">
                        <div className="w-[3px] rounded-full shrink-0 mt-1" style={{ background: accentColor, minHeight: '16px' }} />
                        {typeof item.answer === 'object' && item.answer.list ? (
                          <div className={`text-[14px] leading-relaxed space-y-2 ${isDark ? 'text-[#9CA3AF]' : 'text-[#64748B]'}`}>
                            {item.answer.intro && <p>{item.answer.intro}</p>}
                            <ul className="space-y-2 mt-1">
                              {item.answer.list.map((listItem, idx) => (
                                <li key={idx} className="list-disc list-inside">{listItem}</li>
                              ))}
                            </ul>
                            {item.answer.outro && <p className="mt-2">{item.answer.outro}</p>}
                          </div>
                        ) : (
                          <p className={`text-[14px] leading-relaxed ${isDark ? 'text-[#9CA3AF]' : 'text-[#64748B]'}`}>
                            {item.answer}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
